import React, { useState } from 'react';
import { Box, TextField, Autocomplete, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

/**
 * SkillBoxSearchBar
 * A reusable pill-shaped search bar with:
 *   - Left search icon (visual cue)
 *   - Autocomplete text input
 *   - Red circular search button on the right
 *
 * Props:
 *   @param {Array}    options       - Autocomplete suggestion list: [{ id, label }]
 *   @param {Function} onSearch      - Callback when search is triggered (keyword string)
 *   @param {string}   placeholder   - Input placeholder text
 */
const SkillBoxSearchBar = ({
    options = [],
    onSearch,
    placeholder = "Search...",
}) => {
    const [keyword, setKeyword] = useState('');

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            onSearch?.(keyword.trim());
        }
    };

    const handleSearchClick = () => {
        onSearch?.(keyword.trim());
    };

    const filteredOptions =
        keyword.length >= 2
            ? options.filter(opt =>
                opt.label?.toLowerCase().includes(keyword.toLowerCase())
            )
            : [];

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #E2002B70',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    width: '100%',
                    bgcolor: '#fff',
                }}
            >
                {/* Left search icon */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: 1.5,
                        color: '#aaa',
                        flexShrink: 0,
                    }}
                >
                    <SearchIcon fontSize="medium" />
                </Box>

                {/* Autocomplete input */}
                <Box sx={{ flex: 1 }}>
                    <Autocomplete
                        freeSolo
                        options={filteredOptions}
                        getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.label || ''
                        }
                        inputValue={keyword}
                        onInputChange={(_, newValue) => {
                            setKeyword(newValue);
                            onSearch?.(newValue.trim());
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                onKeyDown={handleKeyDown}
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 0,
                                        backgroundColor: 'transparent',
                                        paddingLeft: '0.4rem',
                                        fontFamily: 'Poppins, sans-serif',
                                        '& fieldset': { border: 'none' },
                                        '& input': {
                                            fontFamily: 'Poppins, sans-serif',
                                        },
                                        '& input::placeholder': {
                                            fontFamily: 'Poppins, sans-serif',
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default SkillBoxSearchBar;
